import { useCallback } from "react"
import PropTypes from "prop-types"
import { map } from "lodash"
import { useDispatch } from "react-redux"
import { Creators } from "model/apiWrapper"
import { useAccountAlias } from "resources/account"
import { combineTanStackStatuses, OK } from "helpers/data-statuses"
import useCombinedQueries from "hooks/combine-queries"

function zipRecursive(zipped, result) {
  if (!zipped) {
    zipped = {
      label: result.label,
      values: {},
    }
  }

  for (const category in result.values) {
    zipped.values[category] = result.values[category]
  }

  // Sometimes a contributor shows up as notHeld in the composition query, but is held in the performance query
  // We want to show these securities as no longer held in the application
  // This is the case for securities that only have a pnl value, and no economic exposure
  if (zipped.notHeld && result.isNoLongerHeld) {
    zipped.isNoLongerHeld = result.isNoLongerHeld
  }

  if (result.children) {
    if (!zipped.children) {
      zipped.children = {}
    }

    for (const childId in result.children) {
      const child = result.children[childId]
      if (!zipped.children[childId]) {
        zipped.children[childId] = {
          id: child.id,
          isFactor: child.isFactor,
          label: child.label,
          notHeld: child.notHeld,
          isUnmodeled: child.isUnmodeled,
          isNoLongerHeld: child.isNoLongerHeld,
          values: {},
          pinnedRowOrder: child.pinnedRowOrder,
          tooltipValue: child.tooltipValue,
        }
      }
      zipRecursive(zipped.children[childId], child)
    }
  }

  return zipped
}

export function zipResults(results) {
  return results.reduce((acc, result) => zipRecursive(acc, result), null)
}

export default function ContributorsDataIntegrator({ gatherers, metrics, Renderer, rendererProps = {} }) {
  const dispatch = useDispatch()
  const account = useAccountAlias()
  const combine = useCallback(results => {
    const status = combineTanStackStatuses(results)
    let data
    if (status === OK) {
      data = zipResults(map(results, r => r.data))
    }

    return { status, data }
  }, [])

  const queries = map(gatherers, ({ key, query, variables, select }) => ({
    queryKey: [key],
    queryFn: () => new Promise((resolve, reject) => {
      dispatch(Creators.graphql(account, query, variables, resolve, reject))
    }),
    select,
  }))

  const { status, data } = useCombinedQueries(queries, combine)

  return <Renderer status={status} data={data} metrics={metrics} {...rendererProps} />
}

ContributorsDataIntegrator.propTypes = {
  gatherers: PropTypes.arrayOf(PropTypes.shape({
    prefix: PropTypes.string.isRequired,
    key: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])).isRequired,
    query: PropTypes.string.isRequired,
    variables: PropTypes.object.isRequired,
    select: PropTypes.func.isRequired,
  })).isRequired,
  metrics: PropTypes.array.isRequired,
  Renderer: PropTypes.func.isRequired,
  rendererProps: PropTypes.object,
}
